import { useState, useRef, KeyboardEvent } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import { useMutation } from '@apollo/client'
import UPDATE_NONPROFIT_ORG_PROFILE from 'Apollo/Mutations/UpdateNonprofitOrgProfile'
import { useAppState } from 'Context/AppContext/AppContext'
import { NTEE } from 'Context/NTEE/ntee'
import { Button } from '@resiliatech/birdsong'
import { IntakeFlowStep } from '../../constants'
import {
  ImpactChipsContainer,
  StyledChip,
  AddChipContainer,
  ImpactAutoComplete,
  AutoCompleteTextField,
  StyledMenuItem,
} from './ImpactAreas.styled'
import { ActionContainer, Container } from '../../IntakeFlow.styled'

type Props = {
  onNextStep: (nextStep: IntakeFlowStep) => void
  onBackStep: () => void
  impactAreas: string[]
  onUpdateImpactAreas: (areas: string[]) => void
}

export const ImpactAreas = ({
  onNextStep,
  onBackStep,
  impactAreas,
  onUpdateImpactAreas,
}: Props) => {
  const [isAddingArea, setIsAddingArea] = useState(false)
  const [newArea, setNewArea] = useState<string>('')
  const inputRef = useRef<HTMLInputElement>(null)
  const { nonprofitOrgConfig } = useAppState()
  const { orgId: nonprofitOrgId } = nonprofitOrgConfig

  const [updateNonprofitOrgProfile] = useMutation(
    UPDATE_NONPROFIT_ORG_PROFILE,
    {
      onError: (error) => {
        console.error('Error updating nonprofit profile:', error)
      },
    }
  )
  const handleDeleteArea = (area: string) => {
    onUpdateImpactAreas(impactAreas.filter((a) => a !== area))
  }

  const handleAddArea = () => {
    setIsAddingArea(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const handleConfirmAdd = () => {
    if (newArea?.trim()) {
      onUpdateImpactAreas([...impactAreas, newArea.trim()])
      setNewArea('')
    }
    setIsAddingArea(false)
  }

  const handleKeyUp = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirmAdd()
    } else if (e.key === 'Escape') {
      setIsAddingArea(false)
      setNewArea('')
    }
  }

  const handleNext = () => {
    onNextStep(IntakeFlowStep.TOP_PRIORITIES)

    if (nonprofitOrgId) {
      updateNonprofitOrgProfile({
        variables: {
          updateNonprofitOrgProfileInput: {
            nonprofitOrgId,
            confirmedAreasOfImpact: impactAreas,
          },
        },
      }).catch((error) => {
        console.error('Error updating nonprofit profile:', error)
      })
    }
  }

  return (
    <Container>
      <ImpactChipsContainer>
        {impactAreas.map((area) => (
          <StyledChip
            key={area}
            label={area}
            deleteIcon={<CloseIcon />}
            onDelete={() => handleDeleteArea(area)}
          />
        ))}
        {isAddingArea ? (
          <AddChipContainer>
            <ImpactAutoComplete
              freeSolo
              ref={inputRef}
              value={newArea}
              onChange={(e, value) => setNewArea(value as string)}
              options={NTEE.filter((value) => !impactAreas.includes(value))}
              onKeyUp={handleKeyUp}
              onBlur={handleConfirmAdd}
              renderInput={(params) => (
                <AutoCompleteTextField
                  variant='standard'
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  InputProps={{ ...params.InputProps, disableUnderline: true }}
                  label=''
                  placeholder='Type here...'
                />
              )}
              renderOption={(props, option, state, ownerState) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <StyledMenuItem {...props}>
                  {ownerState.getOptionLabel(option)}
                </StyledMenuItem>
              )}
            />
          </AddChipContainer>
        ) : (
          <StyledChip
            label='Add'
            onClick={handleAddArea}
            // Using deleteIcon instead of icon prop to position the plus icon at the end of the chip
            onDelete={handleAddArea}
            deleteIcon={<AddIcon />}
          />
        )}
      </ImpactChipsContainer>
      <ActionContainer>
        <Button variant='text' onClick={onBackStep}>
          Back
        </Button>
        <Button variant='primary' onClick={handleNext} label='Next' />
      </ActionContainer>
    </Container>
  )
}
